import { css } from "@tento-ui/component-styles";
import {
  ClaudePromptBlockPlugin,
  ClaudeTextBlockPlugin,
  ClaudeToolCallBlockPlugin,
} from "./claude-blocks";
import { ChatClaudeTurn } from "./claude-turn";
import { ChatThemes } from "../chat-theme";
import type { ChatTheme } from "../chat-theme";

/**
 * The Claude Code look, from the CLI's captured components
 * (brainless.swerdlow.dev/components — the claude-* set): #1a1a1a terminal
 * ground with a #c0caf5 face at 13px, the user's prompt as a full-width
 * #3a3a3a band opened by a dim ❯, assistant prose behind the ⏺ marker, and
 * tool calls in the ⏺ Bash(…) / ⎿ summary grammar the ChatClaudeTurn
 * dialect routes. The status greens/reds and the dim inks are the captured
 * hexes, stated once here; the plugins carry the same values as hook
 * defaults, so this sheet only has to disagree where a different host would.
 */
export const ClaudeCliLook: ChatTheme = {
  ...ChatThemes.tui(),
  name: "claude-cli",
  makeTurn: () => ChatClaudeTurn.make() as ChatClaudeTurn,
  // The dialect this look speaks, carried by the look rather than installed on
  // every thread. `ChatClaudeTurn.pluginTypeFor` routes blocks onto these three
  // types; nothing else in the repo asks for them, so nothing else should have
  // to know they exist.
  plugins: () => [
    new ClaudeTextBlockPlugin(),
    new ClaudePromptBlockPlugin(),
    new ClaudeToolCallBlockPlugin(),
  ],
  $style: css`
    :host {
      background: #1a1a1a;
      color: #c0caf5;
      --text-pri: #c0caf5;
      --text-sec: #8b8fa3;
      --text-ter: #565f89;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 13px;
      line-height: round(nearest, 1.55em, 1px);
      --thread-top-inset: 12px;
      --thread-bottom-inset: 12px;
      --chat-turn-inset: 16px;
      --chat-terminal-font: ui-monospace, SFMono-Regular, Menlo, monospace;
      --chat-terminal-size: 13px;
      --chat-terminal-leading: round(nearest, 1.55em, 1px);
      --session-replay-ansi-ground: #1a1a1a;
      /* The capture's SGR-yellow accent stands down to Claude's own
         terracotta; refs take the reference's cyan; diff roles land on
         the same green/red the tool-call status dots use. */
      --session-replay-ansi-yellow: #cd694a;
      --session-replay-ansi-cyan: #7dcfff;
      --session-replay-ansi-green: #4ea96f;
      --session-replay-ansi-red: #f7768e;
      /* Claude Code breathes exactly as codex does: one blank line
         between a turn's items, one after the turn. */
      --chat-tui-block-gap: 1lh;
      --chat-tui-turn-gap: 1lh;
    }

    /* The prompt row: Claude Code paints the user's turn as a full-width
       #3a3a3a band with white text (the ❯ caret keeps its own dim ink
       from the plugin). Sized to stop above the turn gap so the blank
       line after the band stays the terminal's ground. */
    std-chat-claude-turn[user] {
      color: #ffffff;
      background: linear-gradient(#3a3a3a, #3a3a3a) top / 100%
        calc(100% - var(--chat-tui-turn-gap, 0px)) no-repeat;
    }
  `,
};
